import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Tooltip, Typography, Accordion, AccordionSummary, AccordionDetails,
  List, ListItem, ListItemText, Chip, Divider, CircularProgress,
  Select, MenuItem, FormControl, Stack, TextField, InputAdornment,
  IconButton, Button, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Alert from '@mui/material/Alert';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import api from '../../../../services/api';
import { useTopologyBase } from './topologyApi';
import Timeline from './Timeline';

const CAT_COLOR = {
  BGP: '#e67e22',
  VLAN: '#3498db',
  IP: '#16a085',
  interface: '#8e44ad',
  rota: '#7f8c8d',
  OSPF: '#9b59b6',
  MPLS: '#2dd4bf',
  device: '#c0392b',
  firewall: '#d97706',
  'address-list': '#0891b2',
  pool: '#7c3aed',
};
const KIND_SYM = { added: '+', removed: '−', changed: '~' };
const KIND_COLOR = { added: 'success.main', removed: 'error.main', changed: 'warning.main' };

/**
 * Snapshot de mudança — consome GET /admin/topology/{id}/changes?offset=N.
 * Modo timeline (volume por categoria) + changelog por host (o que mudou do
 * backup anterior pro atual).
 */
export default function ChangesPanel({ }) {
  const base = useTopologyBase();
  const [offset, setOffset] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [datas, setDatas] = useState([]);
  const [modo, setModo] = useState('offset');   // offset | datas | dia
  const [dia, setDia] = useState(null);
  const [barras, setBarras] = useState([]);
  const [carregandoTL, setCarregandoTL] = useState(false);
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [busca, setBusca] = useState('');
  const [catSel, setCatSel] = useState([]);
  const [aberto, setAberto] = useState(null);

  // dias disponíveis, pro seletor não obrigar a adivinhar data
  useEffect(() => {
    let vivo = true;
    if (!base) return undefined;
    api.get(`${base}/changes/datas`)
      .then((res) => {
        if (!vivo) return;
        const ds = res.data?.datas || [];
        setDatas(ds);
        if (ds.length >= 2) { setAte(ds[0].data); setDe(ds[1].data); }
      })
      .catch(() => vivo && setDatas([]));
    return () => { vivo = false; };
  }, [base]);

  // timeline: uma vez por empresa, independente do modo — ela é o mapa do
  // período e continua útil enquanto o operador olha um dia específico
  useEffect(() => {
    let vivo = true;
    if (!base) return undefined;
    setCarregandoTL(true);
    api.get(`${base}/changes/timeline?dias=60`)
      .then((res) => vivo && setBarras(res.data?.barras || []))
      .catch(() => vivo && setBarras([]))
      .finally(() => vivo && setCarregandoTL(false));
    return () => { vivo = false; };
  }, [base]);

  useEffect(() => {
    let vivo = true;
    if (!base) return undefined;
    if (modo === 'datas' && (!de || !ate)) return undefined;
    if (modo === 'dia' && !dia) return undefined;
    setLoading(true);
    setErro(null);
    const q = modo === 'dia' ? `dia=${dia}`
      : modo === 'datas' ? `de=${de}&ate=${ate}`
        : `offset=${offset}`;
    api.get(`${base}/changes?${q}`)
      .then((res) => vivo && setData(res.data))
      .catch((e) => {
        if (!vivo) return;
        setData(null);
        // Antes o erro virava `data = null` e a tela dizia "Sem mudanças no
        // período ✓" — falha disfarçada de boa notícia. Agora aparece.
        setErro(e?.response?.data?.detail || e?.message || 'falha ao carregar mudanças');
      })
      .finally(() => vivo && setLoading(false));
    return () => { vivo = false; };
  }, [base, offset, modo, de, ate, dia]);

  const byCat = data?.by_category || {};
  const perHostBruto = data?.per_host || [];
  const maxCat = Math.max(1, ...Object.values(byCat));

  // Filtro combinado: nome do equipamento e categoria. Roda no cliente porque
  // o payload inteiro tem 25 KB — mandar isso pro servidor seria uma ida de
  // rede pra economizar um filtro em 81 itens.
  const perHost = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return perHostBruto
      .map((ph) => {
        const changes = (ph.changes || []).filter(
          (c) => !catSel.length || catSel.includes(c.cat),
        );
        return {
          ...ph,
          changes,
          count: changes.length,
          linhas_mais: ph.linhas_mais || 0,
          linhas_menos: ph.linhas_menos || 0,
        };
      })
      .filter((ph) => ph.count > 0 || ph.linhas_mais || ph.linhas_menos)
      .filter((ph) => !q || String(ph.host_name || '').toLowerCase().includes(q))
      // Reordena DEPOIS do filtro: ao esconder categorias o count de cada host
      // muda, e manter a ordem que veio do servidor deixaria de responder
      // "quem mudou mais" — que é a pergunta que a ordenação existe pra fazer.
      .sort((x, y) => (y.count - x.count)
        || ((y.linhas_mais + y.linhas_menos) - (x.linhas_mais + x.linhas_menos)));
  }, [perHostBruto, busca, catSel]);

  const totalFiltrado = perHost.reduce((s2, ph) => s2 + ph.count, 0);
  const filtrando = !!busca || !!catSel.length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (erro) {
    return (
      <Paper sx={{ p: 2, height: '100%', bgcolor: '#ffffff' }}>
        <Alert severity="error" icon={<ErrorOutlineIcon />}
               sx={{ alignItems: 'flex-start' }}>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
            Não foi possível calcular as mudanças
          </Typography>
          <Typography sx={{ fontSize: 13, fontFamily: 'monospace',
                            wordBreak: 'break-word', color: '#7f1d1d' }}>
            {String(erro)}
          </Typography>
          <Button size="small" onClick={() => { setErro(null); setOffset(offset); }}
                  sx={{ mt: 1 }}>
            tentar de novo
          </Button>
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{
      p: 2, height: '100%', overflow: 'auto',
      // mesmo tema claro do resto da página — o painel era escuro e destoava
      bgcolor: '#ffffff', color: '#0f172a',
      '& .MuiTypography-root': { color: '#0f172a' },
      '& .MuiInputBase-root': { bgcolor: '#ffffff' },
      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
      '& .MuiSvgIcon-root': { color: '#475569' },
      '& .MuiAccordion-root': { bgcolor: '#ffffff' },
      '& .MuiDivider-root': { borderColor: '#e2e8f0' },
    }}>
      <Timeline
        barras={barras}
        carregando={carregandoTL}
        diaSel={modo === 'dia' ? dia : null}
        cores={CAT_COLOR}
        onSelecionarDia={(d) => {
          if (d) { setDia(d); setModo('dia'); }
          else { setDia(null); setModo('offset'); }
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <HistoryIcon fontSize="small" />
        <Typography variant="subtitle2" fontWeight={700}>
          {filtrando
            ? `${totalFiltrado} de ${data?.total_changes || 0} mudanças · ${perHost.length} de ${data?.hosts_changed || 0} devices`
            : `${data?.total_changes || 0} mudanças em ${data?.hosts_changed || 0} devices`}
        </Typography>
        {(data?.linhas_mais || data?.linhas_menos) ? (
          <Tooltip title={`${data?.hosts_texto || 0} equipamentos mudaram texto de config sem que nada `
            + `que a gente extrai tenha mudado — costuma ser configuração que o parser ainda não lê`}>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'baseline' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#15803d' }}>
                +{data.linhas_mais}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#b91c1c' }}>
                −{data.linhas_menos}
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#64748b' }}>linhas</Typography>
            </Box>
          </Tooltip>
        ) : null}
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder="buscar equipamento…"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          sx={{ width: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
            endAdornment: busca ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setBusca('')} sx={{ p: 0.3 }}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <ToggleButtonGroup size="small" exclusive value={modo}
                           onChange={(_, v) => v && setModo(v)}>
          <ToggleButton value="offset" sx={{ fontSize: 11, py: 0.3 }}>backup</ToggleButton>
          <ToggleButton value="datas" sx={{ fontSize: 11, py: 0.3 }}
                        disabled={datas.length < 2}>datas</ToggleButton>
          <ToggleButton value="dia" sx={{ fontSize: 11, py: 0.3 }}
                        disabled={!dia}>{dia || 'dia'}</ToggleButton>
        </ToggleButtonGroup>
        {modo === 'dia' ? null : modo === 'offset' ? (
          <FormControl size="small">
            <Select value={offset} onChange={(e) => setOffset(e.target.value)} sx={{ fontSize: 12 }}>
              <MenuItem value={1}>vs backup anterior</MenuItem>
              <MenuItem value={2}>vs 2 backups atrás</MenuItem>
              <MenuItem value={5}>vs 5 backups atrás</MenuItem>
            </Select>
          </FormControl>
        ) : (
          <>
            <FormControl size="small">
              <Select value={de} onChange={(e) => setDe(e.target.value)} sx={{ fontSize: 12 }}>
                {datas.map((d) => (
                  <MenuItem key={d.data} value={d.data}>{d.data}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography sx={{ fontSize: 12, color: '#64748b' }}>até</Typography>
            <FormControl size="small">
              <Select value={ate} onChange={(e) => setAte(e.target.value)} sx={{ fontSize: 12 }}>
                {datas.map((d) => (
                  <MenuItem key={d.data} value={d.data}>{d.data}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
      </Box>

      {/* A barra por categoria vira filtro: ver que 'interface' tem 125
          mudanças e não conseguir clicar pra ver quais era metade da
          informação. */}
      <Box sx={{ mb: 2 }}>
        {Object.entries(byCat)
          .sort((a, b) => b[1] - a[1])
          .map(([c, n]) => {
            const ativo = catSel.includes(c);
            return (
              <Box
                key={c}
                onClick={() => setCatSel(ativo ? catSel.filter((x) => x !== c) : [...catSel, c])}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, mb: 0.4,
                  cursor: 'pointer', borderRadius: 0.5, px: 0.5,
                  bgcolor: ativo ? '#e2e8f0' : 'transparent',
                  '&:hover': { bgcolor: '#f1f5f9' },
                }}
              >
                <Typography variant="caption" sx={{
                  width: 70, textAlign: 'right',
                  fontWeight: ativo ? 700 : 400,
                }}>
                  {c}
                </Typography>
                <Box sx={{
                  height: 12, width: `${(n / maxCat) * 100}%`, minWidth: 4,
                  bgcolor: CAT_COLOR[c] || '#888', borderRadius: 0.5,
                  opacity: !catSel.length || ativo ? 1 : 0.35,
                }} />
                <Typography variant="caption" sx={{ color: '#64748b' }}>{n}</Typography>
              </Box>
            );
          })}
        {Object.keys(byCat).length === 0 && (
          <Typography variant="body2" sx={{ color: '#15803d' }}>
            Sem mudanças no período ✓
          </Typography>
        )}
      </Box>

      {filtrando && (
        <Button size="small" startIcon={<ClearIcon fontSize="small" />}
                onClick={() => { setBusca(''); setCatSel([]); }} sx={{ mb: 1 }}>
          limpar filtros
        </Button>
      )}

      <Divider sx={{ mb: 1 }} />

      {!perHost.length && (
        <Typography variant="body2" sx={{ color: '#94a3b8', py: 2 }}>
          {filtrando ? 'Nenhum equipamento com esses filtros.' : 'Nenhuma mudança.'}
        </Typography>
      )}

      {perHost.map((ph) => (
        <Accordion
          key={ph.host_id}
          disableGutters
          // Com busca ativa o resultado costuma ser 1 ou 2 equipamentos, e
          // obrigar a clicar pra ver o que mudou é um clique inútil.
          expanded={aberto === ph.host_id || (!!busca && perHost.length <= 3)}
          onChange={() => setAberto(aberto === ph.host_id ? null : ph.host_id)}
          sx={{
            boxShadow: 'none', '&:before': { display: 'none' },
            border: '1px solid #e2e8f0', borderRadius: 1, mb: 0.5,
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 38 }}>
            <Chip label={ph.count} size="small"
                  sx={{ mr: 1, bgcolor: ph.count ? '#e2e8f0' : '#f8fafc',
                        color: ph.count ? '#0f172a' : '#94a3b8', fontWeight: 700 }} />
            <Typography variant="body2" sx={{ wordBreak: 'break-word', fontWeight: 600, flex: 1 }}>
              {ph.host_name}
            </Typography>
            {(ph.linhas_mais || ph.linhas_menos) ? (
              <Typography sx={{ fontSize: 11, fontFamily: 'monospace', mr: 1,
                                whiteSpace: 'nowrap' }}>
                <span style={{ color: '#15803d' }}>+{ph.linhas_mais}</span>
                {' '}
                <span style={{ color: '#b91c1c' }}>−{ph.linhas_menos}</span>
              </Typography>
            ) : null}
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {!ph.changes.length && (
              <Typography sx={{ fontSize: 12, color: '#b45309' }}>
                Só o texto da config mudou — nada que o parser extrai foi alterado.
                Costuma ser configuração que ainda não lemos.
              </Typography>
            )}
            <List dense disablePadding>
              {(ph.changes || []).map((c, idx) => (
                <ListItem key={idx} disablePadding sx={{ py: 0.2 }}>
                  <Stack direction="row" spacing={0.75} alignItems="baseline">
                    <Typography component="span" sx={{
                      color: KIND_COLOR[c.kind] || '#0f172a',
                      fontWeight: 700, width: 10,
                    }}>
                      {KIND_SYM[c.kind] || '·'}
                    </Typography>
                    <Chip label={c.cat} size="small" sx={{
                      height: 16, fontSize: 10,
                      bgcolor: CAT_COLOR[c.cat] || '#888', color: '#fff',
                    }} />
                    <ListItemText
                      primary={`${c.what}${c.detail ? ` (${c.detail})` : ''}`}
                      primaryTypographyProps={{ variant: 'caption', sx: { wordBreak: 'break-word' } }}
                    />
                  </Stack>
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}
    </Paper>
  );
}
